// spotifyAutoFill.js
// Метод 2: spotify-url-info + прокси, без официального Spotify API

// --- публичное API модуля ---
const DEFAULT_PROXY = '/api/proxy?url=';
const FALLBACK_PROXY = 'https://api.allorigins.win/raw?url=';
const R_JINA_PROXY = 'https://r.jina.ai/';

export async function getSpotifyArtistAndTitle(url, options = {}) {
  const proxyBase = String(options.proxyBase || DEFAULT_PROXY);

  // Загружаем spotify-url-info (UMD с jsDelivr; fallback — ESM с esm.sh)
  const spotifyUrlInfoFactory = await loadSpotifyUrlInfo();

  // Меняем fetch так, чтобы все запросы шли через указанный CORS-прокси
  const proxiedFetch = (u, opts) => fetchThroughProxies(u, opts, proxyBase);

  const { getPreview, getTracks } = spotifyUrlInfoFactory(proxiedFetch);

  // 1) Базовые данные по ссылке
  const data = await getPreview(url);
  let artist = data?.artist || data?.authorName || '';
  let title  = data?.title  || data?.track      || '';

  // 2) Если это /album/ — выполняем требование:
  //   • если альбом = сингл (ровно один трек) → вернуть И артиста, И название песни
  //   • иначе → вернуть только артиста (title = '')
  const isAlbum = /\/album\//.test(url);
  if (isAlbum) {
    try {
      const tracks = await getTracks(url);
      if (Array.isArray(tracks) && tracks.length === 1) {
        const t = tracks[0] || {};
        const pick = (obj, keys) => keys.map(k => obj && obj[k]).find(Boolean);
        const tTitle  = pick(t, ['title','name','track']) || title;
        const tArtist = pick(t, ['artist','artistName','authorName'])
                      || (Array.isArray(t?.artists) ? t.artists.join(', ') : '')
                      || artist;
        title  = tTitle  || '';
        artist = tArtist || artist || '';
      } else {
        // Несколько треков → только артист
        title = '';
      }
    } catch {
      // Не получилось достать треки → только артист
      title = '';
    }
  }

  // 3) Чистим хвосты-приписки
  artist = cleanArtist(artist);
  title  = cleanTitle(title);

  return { artist, title };
}

// Быстрая проверка — это ссылка Spotify?
export function isSpotifyUrl(input) {
  try { const u = new URL(input); return u.hostname.includes('open.spotify.com'); }
  catch { return false; }
}

// --- приватные утилиты ---

function cleanArtist(a) {
  return stripProviderSuffix(a || '');
}
function cleanTitle(t) {
  return stripProviderSuffix(String(t || '').replace(/\s+by\s+.+$/i, '').trim());
}
// Убираем " - Spotify", " | Spotify", " · Spotify", " — Spotify", и " - on Spotify"
function stripProviderSuffix(s = '') {
  return String(s).replace(/\s*[-–—|·•]\s*(?:on\s+)?spotify\s*$/i, '').trim();
}

// Динамическая загрузка spotify-url-info (не требует npm install)
async function loadSpotifyUrlInfo() {
  if (window.spotifyUrlInfo) return window.spotifyUrlInfo;
  // UMD с jsDelivr
  try {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/spotify-url-info@3.2.18/dist/index.umd.min.js';
      s.async = true; s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('UMD load failed'));
      document.head.appendChild(s);
    });
    if (window.spotifyUrlInfo) return window.spotifyUrlInfo;
  } catch {}
  // Fallback: ESM с esm.sh
  const mod = await import('https://esm.sh/spotify-url-info@3.2.18');
  return mod.default || mod;
}

function buildProxyChain(primary) {
  const chain = [];
  const seen = new Set();
  const push = (entry) => {
    const key = `${entry.mode}:${entry.base}`;
    if (seen.has(key)) return;
    seen.add(key);
    chain.push(entry);
  };
  const normalized = String(primary || '').trim();
  if (normalized) push({ base: normalized, mode: 'encode' });
  push({ base: FALLBACK_PROXY, mode: 'encode' });
  push({ base: R_JINA_PROXY, mode: 'passthrough' });
  return chain;
}

function formatProxyUrl(entry, targetUrl) {
  if (entry.mode === 'passthrough') {
    return entry.base.endsWith('/')
      ? `${entry.base}${targetUrl}`
      : `${entry.base}/${targetUrl}`;
  }
  return `${entry.base}${encodeURIComponent(targetUrl)}`;
}

async function fetchThroughProxies(targetUrl, opts, primaryBase) {
  const proxies = buildProxyChain(primaryBase || DEFAULT_PROXY);
  let lastErr;
  for (const entry of proxies) {
    try {
      const finalUrl = formatProxyUrl(entry, targetUrl);
      const resp = await fetch(finalUrl, opts);
      if (!resp || !resp.ok) {
        lastErr = new Error(`Proxy ${entry.base} responded with ${resp ? resp.status : 'ERR'}`);
        continue;
      }
      return resp;
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error('Proxy fetch failed');
}
